// CTV-760: landing page for "Login As -> Self-Serve" in the dashboard's
// Settings -> Users.
//
// The dashboard and self-serve run on different origins but share one Supabase
// project, so the magic-link token the `impersonate-user` edge function mints
// for a super admin verifies here too. The dashboard opens this route in a new
// tab with the one-shot token in the URL fragment (`#token_hash=...`), which
// keeps it out of server and static-host access logs. We redeem it, wipe it
// from the address bar, and hand off to /admin, where PublisherAdminGate
// applies the usual grant + station-scope checks against the new session.
//
// Nothing here is reachable without a valid token: an absent, malformed, or
// already-used one dead-ends on the error card.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { selfServiceSupabase } from '../../lib/selfServiceSupabase';

const ImpersonatePage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // The token is single-use, so the redemption must not run twice. A ref
  // survives StrictMode's double-invoked effect (same component instance),
  // where a cleanup flag alone would let the second pass burn the token.
  const redeemedRef = useRef(false);

  useEffect(() => {
    if (redeemedRef.current) return;
    redeemedRef.current = true;

    const supabase = selfServiceSupabase;
    if (!supabase) {
      setError('Supabase Auth is not configured for this environment.');
      return;
    }

    const tokenHash = new URLSearchParams(
      window.location.hash.replace(/^#/, '')
    ).get('token_hash');

    // Drop the token from the address bar before anything else can read it,
    // whether or not it turns out to be usable.
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`
    );

    if (!tokenHash) {
      setError('This sign-in link is missing its token. Start again from the dashboard.');
      return;
    }

    // Deliberately no cancellation guard and no cleanup. StrictMode's
    // run/cleanup/run cycle would flip such a flag between the two runs, and
    // since the ref stops the second run from re-redeeming, the only in-flight
    // redemption would be discarded and the page would spin forever. Applying
    // the result unconditionally is safe: React 18 no-ops state updates after
    // unmount, and navigating away is what we wanted anyway.
    void (async () => {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      });
      if (verifyError) {
        setError(
          'This sign-in link is invalid or has already been used. Start again from the dashboard.'
        );
        return;
      }
      navigate('/admin', { replace: true });
    })();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <h1 className="text-base font-semibold text-slate-900">Sign-in link not valid</h1>
              <p className="text-sm text-slate-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
      <span className="flex items-center gap-2 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Signing you in…
      </span>
    </div>
  );
};

export default ImpersonatePage;
